/**
 * URL state management for shareable calculator links.
 * Encodes/decodes calculator params in URL query parameters.
 */

const PARAM_MAP = {
  gross_salary: "s",
  kiwisaver_perc: "ks",
  include_student_loan: "sl",
  input_frequency: "freq",
  hours_per_week: "hrs",
  secondary_tax_code: "stc",
  period_start_date: "period",
}

// Legacy aliases accepted when reading (but never written)
const READ_ALIASES = { salary: "gross_salary" }

const REVERSE_MAP = Object.fromEntries(
  Object.entries(PARAM_MAP).map(([k, v]) => [v, k])
)

/**
 * Get search params from either a prefill meta tag or the current URL.
 * Shared result pages inject a <meta name="salary-lab-prefill"> tag with the
 * calculator params so Stimulus controllers can re-calculate on input change.
 *
 * Once the user changes inputs, writeToUrl/writeGenericToUrl switch the URL
 * from the /results/... path to the base calculator path. At that point the
 * URL query params are the source of truth, not the stale prefill meta tag.
 */
function _getSearchParams() {
  // If the URL still points to a /results/ path, use the prefill meta tag
  // (which has the original shared params for initial input population).
  // Once the URL has been replaced with the base calculator path, use the URL.
  if (window.location.pathname.includes("/results/")) {
    const meta = document.querySelector('meta[name="salary-lab-prefill"]')
    if (meta?.content) return new URLSearchParams(meta.content)
  }
  return new URLSearchParams(window.location.search)
}

/**
 * Returns the base calculator path, stripping any /results/... suffix.
 * On a shared page like /cost-of-living/results/abc123/ref this returns
 * /cost-of-living/. On a normal page it returns the pathname as-is.
 */
export function getBaseCalculatorPath() {
  const pathname = window.location.pathname
  const idx = pathname.indexOf("/results/")
  if (idx !== -1) {
    const base = pathname.substring(0, idx)
    return base.endsWith("/") ? base : base + "/"
  }
  return pathname
}

/**
 * Read calculator params from the current URL.
 * @returns {Object} Decoded params (using full param names)
 */
export function readFromUrl() {
  const searchParams = _getSearchParams()
  const params = {}

  for (const [short, long] of Object.entries(REVERSE_MAP)) {
    const value = searchParams.get(short)
    if (value != null) {
      if (long === "include_student_loan") {
        params[long] = value === "true" || value === "1"
      } else if (long === "gross_salary" || long === "kiwisaver_perc" || long === "hours_per_week") {
        params[long] = parseFloat(value) || 0
      } else {
        params[long] = value
      }
    }
  }

  // Accept legacy param names (e.g. ?salary=85000 → gross_salary)
  for (const [legacy, long] of Object.entries(READ_ALIASES)) {
    if (params[long] == null) {
      const value = searchParams.get(legacy)
      if (value != null) {
        params[long] = parseFloat(value) || 0
      }
    }
  }

  return params
}

/**
 * Write calculator params to the URL without page reload.
 * If on a shared result page (/results/...), switches to the base calculator path.
 * @param {Object} params - Calculator params
 */
export function writeToUrl(params) {
  const searchParams = new URLSearchParams()

  for (const [long, short] of Object.entries(PARAM_MAP)) {
    const value = params[long]
    if (value != null && value !== "" && value !== false) {
      if (long === "include_student_loan") {
        searchParams.set(short, value ? "true" : "false")
      } else {
        searchParams.set(short, String(value))
      }
    }
  }

  const basePath = getBaseCalculatorPath()
  const queryString = searchParams.toString()
  const newUrl = queryString
    ? `${basePath}?${queryString}`
    : basePath

  window.history.replaceState(null, "", newUrl)
}

/**
 * Build a query string from calculator params (without leading ?).
 * @param {Object} params - Calculator params
 * @returns {string} Query string (e.g. "s=90000&ks=3")
 */
export function buildQueryString(params) {
  const searchParams = new URLSearchParams()

  for (const [long, short] of Object.entries(PARAM_MAP)) {
    const value = params[long]
    if (value != null && value !== "" && value !== false) {
      searchParams.set(short, String(value))
    }
  }

  return searchParams.toString()
}

/**
 * Build a shareable URL string from params.
 * @param {Object} params
 * @returns {string} Full URL
 */
export function buildShareUrl(params) {
  const qs = buildQueryString(params)
  return `${window.location.origin}${window.location.pathname}?${qs}`
}

/**
 * Per-calculator URL param mappings (fullName → shortName).
 * Used by the share controller to decode URL params back to API names.
 */
const CALCULATOR_URL_MAPS = {
  paye: PARAM_MAP,
  contractor: { rate: "rate", rate_type: "rate_type", working_weeks: "weeks", hours_per_week: "hrs", days_per_week: "days", gst_registered: "gst", expense_percent: "exp", kiwisaver_perc: "ks", include_student_loan: "sl" },
  reverse: { target_net: "net", input_frequency: "freq", hours_per_week: "hrs", kiwisaver_perc: "ks", include_student_loan: "sl" },
  comparison: { a_type: "at", a_salary: "as", a_rate: "ar", a_rate_type: "art", a_working_weeks: "aww", a_hours_per_week: "ahrs", a_expense_percent: "aexp", a_ks: "aks", a_sl: "asl", b_type: "bt", b_salary: "bs", b_rate: "br", b_rate_type: "brt", b_working_weeks: "bww", b_hours_per_week: "bhrs", b_expense_percent: "bexp", b_ks: "bks", b_sl: "bsl" },
  employer_cost: { gross_salary: "s", kiwisaver_rate: "ks", hours_per_week: "hrs", working_weeks: "weeks", training_budget: "training", equipment_budget: "equip" },
  kiwisaver: { current_age: "age", current_salary: "s", current_balance: "bal", contribution_rate: "rate", fund_type: "fund", salary_growth: "growth" },
  student_loan: { loan_balance: "bal", current_salary: "s", salary_growth: "growth", voluntary_payment: "vol", overseas: "overseas" },
  minimum_wage: { wage_type: "wt", hourly_rate: "rate", hours_per_week: "hrs", weeks_per_year: "weeks", kiwisaver_perc: "ks", include_student_loan: "sl" },
  cost_of_living: { current_salary: "s", current_city: "from", target_city: "to", kiwisaver_perc: "ks", include_student_loan: "sl" },
  budget_planner: { take_home: "income", savings_goal_amount: "goal", housing_pct: "h", transport_pct: "tr", groceries_pct: "gr", utilities_pct: "ut", insurance_pct: "in", savings_pct: "sv", debt_pct: "dt", entertainment_pct: "en", clothing_pct: "cl", children_pct: "ch", other_pct: "ot" },
}

/**
 * Read URL params and expand short names to full API param names for sharing.
 * @param {string} calculatorType - Calculator type (e.g. "paye", "contractor")
 * @returns {Object} Params with full API names suitable for the share endpoint
 */
export function readShareParams(calculatorType) {
  const mapping = CALCULATOR_URL_MAPS[calculatorType]
  if (!mapping) return {}

  if (calculatorType === "comparison") {
    return _readComparisonShareParams(mapping)
  }

  const sp = _getSearchParams()
  const params = {}
  for (const [long, short] of Object.entries(mapping)) {
    const raw = sp.get(short)
    if (raw != null) params[long] = raw
  }

  // Accept legacy ?salary= for PAYE
  if (calculatorType === "paye" && !params.gross_salary) {
    const legacy = sp.get("salary")
    if (legacy) params.gross_salary = legacy
  }

  return params
}

function _readComparisonShareParams(mapping) {
  const sp = _getSearchParams()
  const scenarioA = {}
  const scenarioB = {}
  for (const [long, short] of Object.entries(mapping)) {
    const raw = sp.get(short)
    if (raw == null) continue
    if (long.startsWith("a_")) {
      const key = _mapComparisonKey(long.slice(2))
      scenarioA[key] = raw
    } else if (long.startsWith("b_")) {
      const key = _mapComparisonKey(long.slice(2))
      scenarioB[key] = raw
    }
  }
  return { scenario_a: scenarioA, scenario_b: scenarioB }
}

const COMPARISON_KEY_MAP = {
  salary: "gross_salary", ks: "kiwisaver_perc", sl: "include_student_loan",
}
function _mapComparisonKey(key) {
  return COMPARISON_KEY_MAP[key] || key
}

/**
 * Generic URL state: write params to URL using a custom mapping.
 * Used by non-PAYE calculators that have their own param names.
 * If on a shared result page (/results/...), switches to the base calculator path.
 * @param {Object} params - Calculator params (full names)
 * @param {Object} mapping - { fullName: shortName } mapping
 */
export function writeGenericToUrl(params, mapping) {
  const sp = new URLSearchParams()
  for (const [long, short] of Object.entries(mapping)) {
    const value = params[long]
    if (value == null || value === "" || value === false) continue
    sp.set(short, value === true ? "1" : String(value))
  }
  const basePath = getBaseCalculatorPath()
  const qs = sp.toString()
  window.history.replaceState(null, "", qs ? `${basePath}?${qs}` : basePath)
}

/**
 * Generic URL state: read params from URL using a custom mapping.
 * @param {Object} mapping - { fullName: shortName } mapping
 * @param {Object} [types] - Optional type hints: { fullName: "number"|"boolean" }
 * @returns {Object} Decoded params (full names)
 */
export function readGenericFromUrl(mapping, types = {}) {
  const sp = _getSearchParams()
  const params = {}
  for (const [long, short] of Object.entries(mapping)) {
    const raw = sp.get(short)
    if (raw == null) continue
    const t = types[long] || "string"
    if (t === "boolean") params[long] = raw === "1" || raw === "true"
    else if (t === "number") params[long] = parseFloat(raw) || 0
    else params[long] = raw
  }
  return params
}
